import React, { useEffect, useRef, useState } from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle,
    Button, CircularProgress, Typography, Chip, IconButton, Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';


// Steps de ALTO NÍVEL (events.py / @emit_step). add_onu/rem_onu não entram
// aqui: o detalhe deles vem dos comandos (cmdToFriendly), pra mostrar as
// micro-etapas reais (registrando ONT, criando serviço, etc).
const FRIENDLY = {
    connect_olt:    "Acessando o equipamento (OLT)…",
    get_free_onus:  "Procurando equipamento na porta…",
    get_sinal_info: "Lendo sinal do equipamento…",
    list_clients:   "Procurando clientes…",
};

// Traduz um comando bruto (evento "cmd") → micro-status amigável.
// null = ruído (enable/config/quit/etc) → não exibe. Genéricos como
// "enable"/"config" não viram linha; comando não-reconhecido também não
// polui (os steps de alto nível já cobrem o contexto).
function cmdToFriendly(cmd) {
    if (!cmd) return null;
    const c = String(cmd).toLowerCase().trim();
    if (/^(enable|config|quit|)$/.test(c)) return null;
    if (/ont confirm .*sn-auth/.test(c))          return "Registrando equipamento na porta…";
    if (/ont port native-vlan/.test(c))           return "Configurando VLAN do equipamento…";
    if (/service-port vlan/.test(c))              return "Criando o serviço (internet)…";
    if (/undo service-port/.test(c))              return "Removendo o serviço…";
    if (/ont delete/.test(c))                     return "Removendo equipamento da porta…";
    if (/display ont autofind/.test(c))           return "Procurando equipamento na porta…";
    if (/display ont info/.test(c))               return "Localizando o equipamento…";
    if (/ont-srvprofile|ont-lineprofile/.test(c)) return "Validando perfis…";
    if (/display ont optical-info/.test(c))       return "Lendo sinal óptico…";
    if (/interface gpon/.test(c))                 return "Acessando a placa/porta…";
    return null;
}

// Erro: usa o texto de negócio do backend (ev.msg, ex: "O SN não foi
// encontrado") quando é legível; cai no genérico se for stacktrace/técnico.
function friendlyError(raw) {
    if (!raw) return "Não foi possível completar a operação.";
    const s = String(raw).trim();
    if (/Error$|Exception|Traceback|\bat \w+\(/.test(s) || s.length > 160) {
        return "Não foi possível completar a operação. Tente novamente ou contate o suporte.";
    }
    return s;
}


export default function ProvisioningProgressModal({
    open, taskId, mode = "user", onClose, title,
    streamPath,  // ex: "/admin/provisioning/stream" — sem task_id no final
    result,      // { ok, message, info?:[{label,value}], picks?:[{label,value}] }
    onPick,      // (value) => void — chamado ao clicar num pick (ex: SN)
}) {
    const [events, setEvents] = useState([]);
    const [status, setStatus] = useState("running"); // running | done | error
    const esRef = useRef(null);
    const eventsEndRef = useRef(null);

    // Resultado definitivo: o que a tela recebeu do POST manda. Enquanto não
    // chega, segue o status do SSE (progresso ao vivo).
    const finished = result != null || status !== "running";
    const finalState = result != null ? (result.ok ? "done" : "error") : status;

    useEffect(() => {
        if (!open || !taskId) return;
        setEvents([]);
        setStatus("running");

        // Mesmo sentinel do api.js — no build Docker é substituído pelo URL
        // real da API. Em dev REACT_APP_API_URL=http://localhost:8000. Sem
        // isso, em prod vira string relativa, o EventSource cai no nginx do
        // front (que serve index.html → MIME text/html → SSE aborta).
        const baseUrl = (process.env.REACT_APP_API_URL || "__BACKEND_API__") + "/api/v1";
        // EventSource não suporta header Authorization — pega o token do cookie
        // e manda como query param. Backend valida via _auth_admin_from_token.
        const token = document.cookie
            .split(";")
            .map(r => r.trim())
            .find(r => r.startsWith("tokenClient="))
            ?.split("=")[1] || "";
        const url = `${baseUrl}${streamPath}/${taskId}?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url, { withCredentials: true });
        esRef.current = es;

        es.onmessage = (msg) => {
            try {
                const ev = JSON.parse(msg.data);
                setEvents(prev => [...prev, ev]);
                if (ev.step === "operation_complete") {
                    const terminal = ev.status === "error" ? "error"
                                   : ev.status === "done"  ? "done"
                                   : null;
                    // Só atualiza o status do progresso. NÃO fecha sozinha — a
                    // modal mostra o resultado final (com as infos) e o usuário
                    // fecha quando confirmar. Sem toast embaixo.
                    if (terminal) setStatus(terminal);
                }
            } catch (_) { /* ignora linhas inválidas */ }
        };

        es.onerror = () => {
            // EventSource reconecta sozinho — só fecha se já terminou
            if (status !== "running") {
                es.close();
            }
        };

        return () => {
            es.close();
            esRef.current = null;
        };
    }, [open, taskId, streamPath]);

    useEffect(() => {
        // Auto-scroll pro fim conforme eventos chegam. Roda no DialogContent
        // (que é o scrollable real depois do Dialog do MUI).
        eventsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [events]);

    const handleClose = () => {
        esRef.current?.close();
        onClose?.();
    };

    const isAdmin = mode === "admin";

    return (
        <Dialog open={open} onClose={finished ? handleClose : undefined}
                maxWidth={isAdmin ? "md" : "xs"} fullWidth
                scroll="paper"
                PaperProps={{ sx: { maxHeight: '90vh' } }}>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    {!finished && <CircularProgress size={20} />}
                    {finished && finalState === "done" && <CheckCircleIcon color="success" />}
                    {finished && finalState === "error" && <ErrorIcon color="error" />}
                    <Typography variant="h6" component="div">
                        {title || (isAdmin ? `Provisionamento — task ${taskId}` : "Provisionamento")}
                    </Typography>
                    {isAdmin && taskId && (
                        <Tooltip title="Copiar task_id">
                            <IconButton size="small" onClick={() => navigator.clipboard?.writeText(taskId)}>
                                <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {isAdmin
                    ? <AdminLog events={events} />
                    : <UserSummary events={events} status={status} result={result}
                                   onPick={onPick} />}
                <div ref={eventsEndRef} />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={!finished} variant={finished ? "contained" : "text"}>
                    {finished ? "Fechar" : "Aguardando..."}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


function UserSummary({ events, status, result, onPick }) {
    // Modo cliente: lista enxuta de micro-status, sem repetir, com detalhe
    // do erro vindo do backend (ex: "O SN não foi encontrado").
    const shown = [];
    const byStep = new Map();   // step de alto nível -> índice em shown
    let finalError = null;
    let lastCmdIdx = -1;        // índice do último micro-passo (cmd)
    let lastCmdMsg = null;      // dedup de cmd consecutivo igual

    for (const ev of events) {
        if (ev.step === "operation_complete") {
            if (ev.status === "error") finalError = ev.msg || ev.error;
            continue;
        }

        // Micro-passos derivados dos comandos (opção C).
        if (ev.step === "cmd") {
            if (ev.status === "error") {
                if (lastCmdIdx >= 0) {
                    shown[lastCmdIdx].state = "error";
                    shown[lastCmdIdx].detail = friendlyError(ev.error || ev.msg);
                }
                continue;
            }
            const msg = cmdToFriendly(ev.command);
            if (!msg || msg === lastCmdMsg) continue;   // ruído ou repetido
            if (lastCmdIdx >= 0 && shown[lastCmdIdx].state === "running")
                shown[lastCmdIdx].state = "done";
            lastCmdIdx = shown.length;
            lastCmdMsg = msg;
            shown.push({ label: msg, state: "running", detail: null });
            continue;
        }
        if (ev.step === "cmd_done") {
            if (lastCmdIdx >= 0 && shown[lastCmdIdx].state === "running")
                shown[lastCmdIdx].state = "done";
            continue;
        }

        // Steps de alto nível.
        const label = FRIENDLY[ev.step];
        if (!label) continue;
        if (ev.status === "start") {
            if (byStep.has(ev.step)) continue;
            byStep.set(ev.step, shown.length);
            shown.push({ label, state: "running", detail: null });
        } else if (ev.status === "done") {
            const i = byStep.get(ev.step);
            if (i != null) shown[i].state = "done";
        } else if (ev.status === "error") {
            const i = byStep.get(ev.step);
            if (i != null) {
                shown[i].state = "error";
                shown[i].detail = friendlyError(ev.msg || ev.error);
            }
        }
    }

    // Terminou com sucesso → fecha os passos que ficaram "running".
    if ((status === "done" || result?.ok) && !finalError) {
        shown.forEach(s => { if (s.state === "running") s.state = "done"; });
    }

    if ((status === "error" || result?.ok === false) && finalError
        && !shown.some(s => s.state === "error")) {
        shown.push({ label: "Operação não concluída", state: "error",
                     detail: friendlyError(finalError) });
    }

    return (
        <Box>
            {shown.map((s, i) => (
                <Box key={i} display="flex" alignItems="flex-start" gap={1} py={0.5}>
                    {s.state === "running" && <HourglassEmptyIcon fontSize="small" color="action" />}
                    {s.state === "done" && <CheckCircleIcon fontSize="small" color="success" />}
                    {s.state === "error" && <ErrorIcon fontSize="small" color="error" />}
                    <Box>
                        <Typography variant="body2">{s.label}</Typography>
                        {s.detail && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                {s.detail}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
            {status === "running" && !result && shown.length === 0 && (
                <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Acessando o equipamento…</Typography>
                </Box>
            )}

            {/* Resultado definitivo vindo da tela (substitui o toast). */}
            {result && (
                <Box mt={1.5} p={1.5} borderRadius={1}
                     sx={{ bgcolor: result.ok ? 'success.50' : 'error.50',
                            border: 1, borderColor: result.ok ? 'success.light' : 'error.light' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={result.info?.length ? 1 : 0}>
                        {result.ok
                            ? <CheckCircleIcon fontSize="small" color="success" />
                            : <ErrorIcon fontSize="small" color="error" />}
                        <Typography variant="body2" fontWeight={700}
                                    color={result.ok ? 'success.dark' : 'error.dark'}>
                            {result.message || (result.ok ? "Concluído com sucesso" : "Operação não concluída")}
                        </Typography>
                    </Box>
                    {result.ok && result.info?.length > 0 && (
                        <Box component="table" sx={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                            <tbody>
                                {result.info.filter(r => r.value !== undefined && r.value !== null && r.value !== "")
                                    .map((r, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: '2px 8px 2px 0', color: '#5f6368', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                                            {r.label}
                                        </td>
                                        <td style={{ padding: '2px 0', fontWeight: 600, wordBreak: 'break-all' }}>
                                            {String(r.value)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Box>
                    )}
                    {result.ok && result.picks?.length > 0 && (
                        <Box mt={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Clique pra selecionar:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {result.picks.map((p, i) => (
                                    <Chip key={i} label={p.label ?? p.value} size="small"
                                          color="primary" variant="outlined" clickable
                                          onClick={() => onPick?.(p.value ?? p)} />
                                ))}
                            </Box>
                        </Box>
                    )}
                    {result.ok && result.picks?.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Nenhum equipamento livre encontrado nessa porta.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}


// Cores estilo terminal (paleta tipo xterm "Solarized Dark"-ish)
const TERM_COLORS = {
    bg: '#0d1117',                  // fundo terminal
    text: '#c9d1d9',                // texto base
    prompt: '#7ee787',              // verde prompt
    cmd: '#79c0ff',                 // azul comando
    output: '#c9d1d9',              // output cru
    dim: '#6e7681',                 // metadados (timestamp)
    success: '#3fb950',
    error: '#f85149',
    warn: '#d29922',
    info: '#58a6ff',
};

/**
 * Extrai o prompt do equipamento do final do response.
 * Ex: "...\n10012-OLT-HUAWEI#" → "10012-OLT-HUAWEI#"
 *     "...\n10012-OLT-HUAWEI(config-if-gpon-0/1)#" → idem
 *     "...\n10012-OLT-HUAWEI>" → idem (user mode)
 * Reconhece terminadores `#` (privileged/config) e `>` (user).
 */
function extractPrompt(response) {
    if (!response) return null;
    const lines = response.split('\n').map(l => l.trim()).filter(Boolean);
    for (let i = lines.length - 1; i >= 0; i--) {
        const l = lines[i];
        // matches: HOST# / HOST> / HOST(config)# / HOST(config-if-gpon-0/1/2)#
        if (/^[\w.-]+(\([\w/.-]+\))?\s*[#>]$/.test(l)) {
            return l;
        }
    }
    return null;
}


function AdminLog({ events }) {
    // Modo admin: terminal-style. Mantém estado de prompt entre comandos —
    // após `enable` o response termina com `HOST#`, então próximo `cmd` aparece
    // como `HOST# <command>` em vez de placeholder `$ <command>`.
    if (events.length === 0) {
        return (
            <Box sx={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
                fontSize: 12.5, bgcolor: TERM_COLORS.bg, color: TERM_COLORS.text,
                p: 2, borderRadius: 1, minHeight: 200,
            }}>
                <span style={{ color: TERM_COLORS.dim }}>aguardando primeiro evento…</span>{' '}
                <span style={{ color: TERM_COLORS.prompt }}>▊</span>
            </Box>
        );
    }

    // Single-pass: vai construindo lista de linhas e atualizando prompt corrente
    let currentPrompt = "$";   // placeholder antes do primeiro response
    const rendered = events.map((ev, i) => {
        const ts = ev.ts ? new Date(ev.ts).toLocaleTimeString('pt-BR') : "";

        // Comando enviado: usa o prompt corrente
        if (ev.step === "cmd" && ev.command) {
            const prompt = currentPrompt;
            if (ev.status === "error") {
                return (
                    <div key={i}>
                        <span style={{ color: TERM_COLORS.dim }}>[{ts}] </span>
                        <span style={{ color: TERM_COLORS.prompt }}>{prompt} </span>
                        <span style={{ color: TERM_COLORS.cmd, textDecoration: 'line-through' }}>
                            {ev.command}
                        </span>
                        {ev.error && (
                            <div style={{ color: TERM_COLORS.error, paddingLeft: 8 }}>
                                ✗ {ev.error}
                            </div>
                        )}
                    </div>
                );
            }
            return (
                <div key={i}>
                    <span style={{ color: TERM_COLORS.dim }}>[{ts}] </span>
                    <span style={{ color: TERM_COLORS.prompt }}>{prompt} </span>
                    <span style={{ color: TERM_COLORS.cmd }}>{ev.command}</span>
                </div>
            );
        }

        // Response: imprime cru e atualiza prompt corrente
        if (ev.step === "cmd_done" && ev.response !== undefined) {
            const newPrompt = extractPrompt(ev.response);
            if (newPrompt) currentPrompt = newPrompt;
            return (
                <div key={i} style={{ marginBottom: 6 }}>
                    {ev.response && (
                        <Box component="pre" sx={{
                            m: 0, fontFamily: 'inherit', fontSize: 'inherit',
                            color: TERM_COLORS.output, whiteSpace: 'pre-wrap',
                        }}>{ev.response}</Box>
                    )}
                    {ev.truncated && (
                        <div style={{ color: TERM_COLORS.warn, fontStyle: 'italic' }}>
                            … (truncado em 5000 chars, total real: {ev.response_len})
                        </div>
                    )}
                </div>
            );
        }

        // Steps de estado (operation_complete, connect_olt, get_lines, etc)
        const stateColor = ev.status === "error" ? TERM_COLORS.error
                         : ev.status === "done"  ? TERM_COLORS.success
                         : ev.status === "start" ? TERM_COLORS.info
                         : TERM_COLORS.text;
        const tag = ev.step === "operation_complete" ? "▣ operation"
                  : `▸ ${ev.step}`;
        const extra = Object.entries(ev)
            .filter(([k]) => !["ts","task_id","step","status","response","response_len","truncated","command"].includes(k))
            .map(([k,v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
            .join(' ');

        return (
            <div key={i}>
                <span style={{ color: TERM_COLORS.dim }}>[{ts}] </span>
                <span style={{ color: stateColor }}>{tag}</span>
                {ev.status && <span style={{ color: TERM_COLORS.dim }}>:{ev.status}</span>}
                {extra && <span style={{ color: TERM_COLORS.dim }}> {extra}</span>}
            </div>
        );
    });

    return (
        <Box sx={{
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
            fontSize: 12.5, lineHeight: 1.45,
            bgcolor: TERM_COLORS.bg, color: TERM_COLORS.text,
            p: 2, borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
            {rendered}
            <span style={{ color: TERM_COLORS.prompt }}>{currentPrompt} ▊</span>
        </Box>
    );
}
